import { useMutation, useQueryClient } from '@tanstack/react-query'
import { bomCapturaApi } from '@/services/api'
import type { BOMCompleto, CapturaBOM, CapturaCreateRequest, CapturaUpdateRequest } from '@/types/bomCaptura'

type BomCache = { data?: BOMCompleto } | undefined

/** CRUD + reordenación de capturas (características / manipulación). */
export function useCapturas(itemId: string, empresa: string, formula: string) {
  const qc = useQueryClient()
  const key = ['bom', itemId, empresa, formula]
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: key })
    // El catálogo "copiar de otra versión" también refleja los cambios (nombre/texto).
    qc.invalidateQueries({ queryKey: ['bom-catalogo-caracteristicas'] })
  }

  const add = useMutation({
    mutationFn: (body: CapturaCreateRequest) => bomCapturaApi.addCaptura(itemId, empresa, formula, body),
    onSuccess: invalidate,
  })
  const update = useMutation({
    mutationFn: (v: { id: string; body: CapturaUpdateRequest }) =>
      bomCapturaApi.updateCaptura(itemId, empresa, formula, v.id, v.body),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: (id: string) => bomCapturaApi.deleteCaptura(itemId, empresa, formula, id),
    onSuccess: invalidate,
  })

  /** Reasigna secuencia 1..n según el nuevo orden y PATCHea las que cambiaron.
   *  Optimista: aplica el orden en la caché al instante y no invalida (evita que el
   *  refetch —con la secuencia aún sin propagar— revierta el cambio). */
  const reorder = useMutation({
    mutationFn: async (ordenadas: CapturaBOM[]) => {
      await Promise.all(
        ordenadas.map((c, i) =>
          c.secuencia === i + 1
            ? Promise.resolve()
            : bomCapturaApi.updateCaptura(itemId, empresa, formula, c.id, { secuencia: i + 1 }),
        ),
      )
    },
    onMutate: async (ordenadas: CapturaBOM[]) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<BomCache>(key)
      qc.setQueryData<BomCache>(key, (old) => {
        if (!old?.data) return old
        const reordenadas = ordenadas.map((c, i) => ({ ...c, secuencia: i + 1 }))
        const otras = old.data.capturas.filter((c) => c.tipo !== 'manipulacion')
        return { ...old, data: { ...old.data, capturas: [...otras, ...reordenadas] } }
      })
      return { prev }
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(key, ctx.prev)
    },
  })

  return { add, update, remove, reorder }
}
